import { Controller } from "stimulus"

// A donor question behind a multi-choice gate (Client::DonorQuestion#gate_options), e.g. "Campus
// chapter / Community Ambassador / Neither" -- see donation_flows/_donor_question_gate_choices.
// Choosing shows that choice's dropdown (if it has one) and hides the rest; a listed option picked
// in the shown dropdown is copied into the question's one named field (with the attribution source
// it identifies), so a hidden dropdown's answer is never submitted. Typing only filters: text
// that doesn't exactly match a listed option syncs nothing, and the named field -- which carries
// the wizard's required target while a dropdown is shown -- stays empty and fails validation.
export default class extends Controller {
  static targets = ["choice", "branch", "output", "sourceType", "sourceId"]

  // The page can come back with a choice already made (back/forward), with radio-select showing
  // its button active -- show its dropdown to match, keeping what was typed in it.
  connect() {
    if (!this.choiceTarget.value) return

    this.branchTargets.forEach((branch) => branch.classList.toggle("is-hidden", branch.dataset.choice !== this.choiceTarget.value))
    this.sync()
  }

  choose() {
    const selected = this.choiceTarget.value
    this.branchTargets.forEach((branch) => {
      const shown = branch.dataset.choice === selected
      branch.classList.toggle("is-hidden", !shown)
      if (!shown) this.inputIn(branch).value = ""
    })
    this.sync()
  }

  sync() {
    const shown = this.branchTargets.find((branch) => !branch.classList.contains("is-hidden"))
    const option = shown ? this.listedOptionIn(shown) : null

    this.outputTarget.value = option ? option.textContent.trim() : ""
    // Answered: drop the wizard's required-field flag (shown on the dropdown, see donation_flow.scss).
    if (option) this.outputTarget.classList.remove("is-danger")
    if (this.hasSourceTypeTarget) this.sourceTypeTarget.value = option ? option.dataset.sourceType : ""
    if (this.hasSourceIdTarget) this.sourceIdTarget.value = option ? option.dataset.sourceId : ""
    this.updateRequired(shown)
  }

  // The listed option the shown dropdown's text exactly matches, if any. A click on an option
  // fills the input with its label first (filterable-dropdown#select), so picking and typing the
  // full name both land here; anything else is not an answer.
  listedOptionIn(branch) {
    const text = this.inputIn(branch).value.trim()
    if (!text) return null

    return Array.from(branch.querySelectorAll("li")).find((item) => item.textContent.trim() === text) || null
  }

  // While a dropdown is shown, its answer is required through the named field (only fillable by
  // a listed option); with no dropdown shown ("Neither"), nothing is required of it. The wizard
  // reads data-donation-wizard-target when it validates, so moving it here is enough.
  updateRequired(shown) {
    const requiredTarget = shown ? this.inputIn(shown).dataset.requiredTarget : null
    if (requiredTarget) {
      this.outputTarget.setAttribute("data-donation-wizard-target", requiredTarget)
    } else {
      this.outputTarget.removeAttribute("data-donation-wizard-target")
    }
  }

  inputIn(branch) {
    return branch.querySelector("input")
  }
}
